import { FileData } from 'app/components/MultipleFileUploadCard';
import { useErrorHandlingSlice } from 'app/error/slice';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import useApi from 'services/mavapi/useApi';
import {
  ManageSponsorService,
  Sponsor,
  SponsorDetail,
  SponsorWrapper,
} from 'services/openapi';
import { useNavigateToRoute } from 'utils/common';

const useSponserApi = () => {
  const redirect = useNavigateToRoute();
  const [loading, setLoading] = useState(false);
  const {
    manageSponsorExportSponsorsList,
    manageSponsorGetSponsors,
    manageSponsorEditSponser,
    manageSponsorAddSponser,
    manageSponsorDeleteSponsor,
    manageSponsorUploadSponsorPhotos,
    manageSponsorGetSponsor,
    manageSponsorDeleteSponsorPhotos,
  } = ManageSponsorService;
  const [, setError] = useState<unknown | null>();
  const { error: apiError, handleRequest } = useApi();
  const { actions } = useErrorHandlingSlice();
  const dispatch = useDispatch();
  const [sponsersDataList, setSponsersDataList] = useState<SponsorWrapper>();
  const [viewSponser, setViewSponser] = useState<SponsorDetail>();
  const fetchSponsersList = (
    searchInput?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber: number = 0,
    pageSize: number = 10,
  ) => {
    setLoading(true);
    let pageNumCall = pageNumber ? Math.floor(pageNumber / pageSize) + 1 : 1;
    handleRequest(
      manageSponsorGetSponsors(
        searchInput,
        sortBy,
        sortOrder,
        pageNumCall,
        pageSize,
      ),
    )
      .then(res => {
        if (res) {
          setSponsersDataList(res);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };
  const downloadSponsersList = (
    searchInput?: string,
    sortBy?: string,
    sortOrder?: number,
  ): Promise<string> => {
    setLoading(true);
    return new Promise((resolve, reject) => {
      handleRequest(
        manageSponsorExportSponsorsList(searchInput, sortBy, sortOrder),
      )
        .then(res => {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Sponsor Downloaded Successfully',
              show: true,
              errorType: 'success',
            }),
          );
          resolve(res);
          setLoading(false);
        })
        .catch(() => {
          reject(apiError);
          setLoading(false);
        });
    });
  };
  const getSponser = (id?: string) => {
    setLoading(true);
    handleRequest(manageSponsorGetSponsor(id ?? ''))
      .then(res => {
        if (res) {
          setViewSponser(res);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };
  const deleteSponsor = (
    sponsorId?: string,
    searchInput?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber: number = 0,
    pageSize: number = 10,
  ) => {
    setLoading(true);
    handleRequest(manageSponsorDeleteSponsor(sponsorId ?? ''))
      .then(res => {
        if (res) {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Sponsor deleted successfully',
              show: true,
              errorType: 'success',
            }),
          );
          // Calculate the correct page after deletion
          // If current page is empty after deletion, go to previous page
          const itemsOnCurrentPage = sponsersDataList?.sponsors?.length ?? 0;
          let nextPageNumber = pageNumber;

          // If we're on a page with only 1 item and we just deleted it, go to previous page
          if (itemsOnCurrentPage <= 1 && pageNumber > 0) {
            nextPageNumber = pageNumber - pageSize;
          }

          fetchSponsersList(
            searchInput ?? '',
            sortBy,
            sortOrder,
            nextPageNumber,
            pageSize,
          );
        }
        setLoading(false);
      })
      .catch(apiError => {
        setError(apiError);
        setLoading(false);
      });
  };
  const deleteSponsorPhotos = (
    sponsorId?: string,
    requestBody?: Array<string>,
  ) => {
    setLoading(true);
    handleRequest(
      manageSponsorDeleteSponsorPhotos(sponsorId ?? '', requestBody),
    )
      .then(res => {
        if (res) {
        }
        setLoading(false);
      })
      .catch(apiError => {
        setError(apiError);
        setLoading(false);
      });
  };
  const updateSponsor = (
    requestBody: Sponsor,
    selectedFiles?: FileData[],
    photoIds?: Array<string>,
  ) => {
    setLoading(true);
    handleRequest(manageSponsorEditSponser(requestBody))
      .then(res => {
        if (!res.code) {
          setLoading(false);
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Sponsor Updated Successfully',
              show: true,
              errorType: 'success',
            }),
          );
          if (photoIds?.length! > 0) {
            deleteSponsorPhotos(requestBody.id as string, photoIds);
          }
          if (selectedFiles?.length! > 0) {
            let formData = {
              files: selectedFiles
                ?.filter(file => file.file !== undefined)
                .map(file => file.file) as Blob[],
            };

            updateOrCreateSponsorImage(res, formData, 'edit');
          }
          redirect(`/admin/sponsors`);
        } else {
          setLoading(false);
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Sponsor Creation Failed',
              show: true,
              errorType: 'error',
            }),
          );
        }
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };
  const handleCreateSponsor = (
    requestBody: Sponsor,
    selectedFiles?: FileData[],
  ) => {
    setLoading(true);
    handleRequest(manageSponsorAddSponser(requestBody))
      .then(res => {
        if (!res.code) {
          setLoading(false);
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Sponsor updated Successfully',
              show: true,
              errorType: 'success',
            }),
          );
          if (selectedFiles?.length! > 0) {
            let formData = {
              files: selectedFiles
                ?.filter(file => file.file !== undefined)
                .map(file => file.file) as Blob[],
            };

            updateOrCreateSponsorImage(res, formData, 'create');
          }
          redirect(`/admin/sponsors`);
        } else {
          setLoading(false);
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Sponsor Creation Failed',
              show: true,
              errorType: 'error',
            }),
          );
        }
      })
      .catch(() => {
        setError(apiError);
      });
  };
  const updateOrCreateSponsorImage = (
    id: string,
    formData?: {
      files?: Array<Blob>;
    },
    action?: string,
  ) => {
    setLoading(true);
    handleRequest(manageSponsorUploadSponsorPhotos(id, formData))
      .then(res => {
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };
  return {
    fetchSponsersList,
    sponsersDataList,
    loading,
    updateSponsor,
    handleCreateSponsor,
    deleteSponsor,
    viewSponser,
    getSponser,
    deleteSponsorPhotos,
    downloadSponsersList,
  };
};
export default useSponserApi;
